import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AutoDemoReason } from '../services/demoMode';

/**
 * App-wide user settings that must outlive a page load.
 *
 * Persisted with the `persist` middleware over the default (synchronous)
 * localStorage adapter, so the state is already hydrated by the time module
 * initialisation finishes. That matters for `demoMode`: the NATS connection and
 * the chat API headers are built from it, and both are established well after
 * module load but must never see the pre-hydration default.
 *
 * Only `demoMode` is persisted (see `partialize`). The automatic demo state is
 * deliberately session-scoped: it is re-evaluated from the token every session
 * and must never be remembered across reloads, or a token change would be
 * shadowed by a stale "auto demo" decision.
 */

/**
 * Same key as `DEMO_AUTO_OPTOUT_KEY` in `services/demoMode.ts`, inlined here to
 * avoid a store↔service import cycle at module-init time. `setDemoModeAndReload`
 * writes it; the store reads it on creation so the opt-out survives the reload
 * that switch performs.
 */
const DEMO_AUTO_OPTOUT_KEY = 'demo-auto-optout';

function readOptOut(): boolean {
  try {
    return sessionStorage.getItem(DEMO_AUTO_OPTOUT_KEY) === '1';
  } catch {
    return false;
  }
}

interface SettingsStore {
  /**
   * Route the whole app at the shared demo dataset instead of the user's own
   * organisation. See `src/services/demoMode.ts` for what this changes.
   * This is the MANUAL switch and the only persisted field.
   */
  demoMode: boolean;
  setDemoMode: (enabled: boolean) => void;

  // --- session-scoped automatic demo (NOT persisted) ---
  /**
   * The automatic fallback flag. `isDemoMode()` returns
   * `demoMode || autoDemoMode`, so this turns the app demo without touching the
   * user's persisted manual choice.
   */
  autoDemoMode: boolean;
  /** Why automatic demo turned on, for the badge wording. */
  autoDemoReason: AutoDemoReason | null;
  /**
   * One-way-within-a-session latch. Once an auto-demo decision has been made
   * (enabled, or declined because demo was already active / the user opted
   * out), this is `true` for the rest of the session and `enableAutoDemo`
   * becomes a no-op. This is the STRUCTURAL guard that makes "at most one
   * automatic switch per session" true by construction, not by a condition that
   * merely happens to be false.
   */
  autoDemoLatched: boolean;
  /**
   * The user turned demo OFF this session. Automatic demo must honour this and
   * not re-enable. Initialised from the sessionStorage marker so it survives
   * the reload `setDemoModeAndReload` performs.
   */
  manualDemoOptOut: boolean;
  /** One-way auto-enable transition — the ONLY writer of `autoDemoMode: true`. */
  enableAutoDemo: (reason: AutoDemoReason) => void;
  setManualDemoOptOut: () => void;
}

export const useSettingsStore = create<SettingsStore>()(
  persist(
    (set) => ({
      demoMode: false,
      setDemoMode: (enabled: boolean) => set({ demoMode: enabled }),

      autoDemoMode: false,
      autoDemoReason: null,
      autoDemoLatched: false,
      manualDemoOptOut: readOptOut(),

      enableAutoDemo: (reason: AutoDemoReason) =>
        set((s) => {
          // Already decided this session — never re-arm. This single guard is
          // what prevents a switch loop even when the demo dataset itself comes
          // back empty/unavailable.
          if (s.autoDemoLatched) return s;
          // Demo already active (manual or auto): nothing to do but latch so we
          // never reconsider.
          if (s.demoMode || s.autoDemoMode) return { autoDemoLatched: true };
          // User opted out this session: honour it and latch.
          if (s.manualDemoOptOut) return { autoDemoLatched: true };
          return {
            autoDemoMode: true,
            autoDemoReason: reason,
            autoDemoLatched: true,
          };
        }),

      setManualDemoOptOut: () => set({ manualDemoOptOut: true }),
    }),
    {
      name: 'settings-store',
      // Persist ONLY the manual switch; the automatic state is session-scoped.
      partialize: (s) => ({ demoMode: s.demoMode }),
    }
  )
);
